import { useCallback } from "react";
import { useDevices } from "./useDevices";
import { useDeviceMutations } from "./useDeviceMutations";

/**
 * Convenience hook combining device loading and mutation capabilities.
 * Mutations automatically trigger a reload so the read state stays in sync.
 *
 * @param {{ storageAccount: string, microgridName: string, datasetName: string }} params
 */
export function useDevicesWithMutations({
  storageAccount,
  microgridName,
  datasetName,
}) {
  const {
    sysconfig,
    devices,
    loading,
    error: loadError,
    reload,
  } = useDevices({ storageAccount, microgridName, datasetName });

  const {
    saving,
    error: mutationError,
    addDevice: rawAddDevice,
    updateDevice: rawUpdateDevice,
    deleteDevice: rawDeleteDevice,
  } = useDeviceMutations({ storageAccount, microgridName, datasetName });

  const addDevice = useCallback(
    async (...args) => {
      await rawAddDevice(...args);
      await reload();
    },
    [rawAddDevice, reload],
  );

  const updateDevice = useCallback(
    async (...args) => {
      await rawUpdateDevice(...args);
      await reload();
    },
    [rawUpdateDevice, reload],
  );

  const deleteDevice = useCallback(
    async (...args) => {
      await rawDeleteDevice(...args);
      await reload();
    },
    [rawDeleteDevice, reload],
  );

  return {
    sysconfig,
    devices,
    loading,
    saving,
    error: loadError || mutationError,
    reload,
    addDevice,
    updateDevice,
    deleteDevice,
  };
}
